import type { Config } from 'constants/config';

// Where the header language toggle leads (CMS-2008): the same path in the brand's other locale.
// Pure, so the header and the 404 page ask it on the client too. Whether the brand has another
// locale at all is the server context's call (`langPrefix`, empty on a single-locale brand): callers
// check that first.

export type LocaleConfig = Pick<Config, 'locales' | 'defaultLocale'>;

type OtherLocale = {
  /** Language tag: `zh-hk`. */
  lang: string;
  /** The same path in it: `/en/credit-card/all` → `/zh/credit-card/all`. */
  path: string;
};

/** The same path in the other locale; `null` when the brand's locales name no other. */
export function getOtherLocale(
  path: string,
  config: LocaleConfig,
): OtherLocale | null {
  const locales = config.locales ?? {};
  const prefixes = Object.keys(locales);
  const [, first = '', ...rest] = path.split('/');
  // A path without a prefix is served in the default locale: /credit-card/all is the ZH page.
  const prefixed = prefixes.includes(first);
  const current = prefixed
    ? first
    : prefixes.find((prefix) => locales[prefix] === config.defaultLocale);
  const other = prefixes.find((prefix) => prefix !== current);
  if (!other) return null;

  const pathInLocale = prefixed ? `/${rest.join('/')}` : path;
  return {
    lang: locales[other],
    path: pathInLocale === '/' ? `/${other}` : `/${other}${pathInLocale}`,
  };
}
